import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Body, Button, Toggle } from "@songtradr-one/components-frontend";
import { AppStorage } from "../../helpers/app-storage";
import { telemetryBus } from "../../helpers/telemetry-bus";
import { Flex } from "../flex/Flex.Parts";
import {
  BoltIcon,
  ContentArea,
  Controls,
  CountBadge,
  EmptyState,
  FloatingButton,
  Group,
  Item,
  JsonContainer,
  MinifiedDot,
  Panel,
  PanelCounter,
  PanelHeader,
  PanelTimestamp,
  PanelTitle,
  SlowMarkerPill,
  TelemetryMessage,
  TimingBar,
  TimingContainer,
  TimingDescription,
  TimingDuration,
  TimingEntry,
  TimingName,
  TimingTrack,
  parseServerTiming,
  prettyJSON,
  text,
} from "./ServerTimingHud.Parts";

export const ServerTimingHud = memo(() => {
  const [items, setItems] = useState<Item[]>([]);
  const [open, setOpen] = useState(false);
  const [_unread, setUnread] = useState(0);
  const [index, setIndex] = useState(0);
  const [showResponse, setShowResponse] = useState(false);
  const [minified, setMinified] = useState(
    () => AppStorage.serverTimingMinified,
  );
  const iconRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openRef = useRef(open);

  // Keep openRef in sync with open state
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const unsub = telemetryBus.subscribe((msg: TelemetryMessage) => {
      if (msg.kind !== "server-timing") return;
      const id = crypto.randomUUID();
      const item: Item = {
        id,
        text: msg.text,
        raw: msg.raw,
        json: msg.jsonResult,
        ts: Date.now(),
      };
      setItems((prev) => [item, ...prev]);
      if (!openRef.current) setUnread((u) => u + 1);
    });

    return () => {
      unsub?.();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !iconRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
        setUnread(0);
      }
    };
    const handleKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handleDocClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next) setUnread(0);
      return next;
    });
  };

  const groupKey = (i: Item) => `${i.text}__${i.raw ?? ""}`;
  const groups: Group[] = useMemo(() => {
    const map = new Map<string, Group>();
    for (const it of items) {
      const key = groupKey(it);
      const g = map.get(key);
      if (g) {
        g.items.push(it);
        if (it.ts > g.latestTs) g.latestTs = it.ts;
      } else {
        map.set(key, {
          key,
          text: it.text,
          raw: it.raw,
          latestTs: it.ts,
          items: [it],
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.latestTs - a.latestTs);
  }, [items]);

  useEffect(() => {
    if (index >= groups.length) setIndex(0);
    setShowResponse(false);
  }, [groups, index]);

  return (
    <>
      {items.length > 0 && minified && !open && (
        <MinifiedDot
          onClick={toggleOpen}
          title={`${items.length} ${text.buttons.serverEvents}`}
        />
      )}

      {items.length > 0 && !minified && (
        <FloatingButton
          ref={iconRef}
          onClick={toggleOpen}
          $isOpen={open}
          $hasItems={items.length > 0}
          title={`${items.length} ${text.buttons.serverEvents}`}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <BoltIcon />
          <CountBadge>{items.length > 99 ? "99+" : items.length}</CountBadge>
        </FloatingButton>
      )}

      {open && groups.length > 0 && (
        <Panel ref={panelRef} role="dialog" aria-label={text.panelTitle}>
          {(() => {
            const safeIndex = Math.min(
              Math.max(0, index),
              Math.max(0, groups.length - 1),
            );

            if (groups.length === 0) {
              return <EmptyState>No issues.</EmptyState>;
            }

            const g = groups[safeIndex]; // <-- use safeIndex
            const entries = parseServerTiming(g?.raw ?? null); // <-- defensively pass null

            const maxDur = Math.max(...entries.map((e) => e.dur ?? 0), 0);

            const latestWithJson = g.items
              .slice()
              .sort((a, b) => b.ts - a.ts)
              .find((i) => typeof i.json !== "undefined");
            const hasJson = !!latestWithJson;
            const jsonText = hasJson ? prettyJSON(latestWithJson.json) : "";

            return (
              <>
                <PanelHeader>
                  <PanelTitle>
                    {text.panelTitle}
                    <PanelTimestamp>
                      {text.panelTimestamp}{" "}
                      {new Date(g.latestTs).toLocaleTimeString()}
                    </PanelTimestamp>
                  </PanelTitle>
                  <PanelCounter>
                    {safeIndex + 1} / {groups.length}
                  </PanelCounter>
                  <Flex $align="center">
                    <Body variant="small">{text.buttons.minimize}</Body>
                    <Toggle
                      checked={minified}
                      onCheckedChange={(checked) => {
                        setMinified(checked);
                        AppStorage.serverTimingMinified = checked;
                      }}
                      size="xsmall"
                    />
                  </Flex>
                </PanelHeader>

                <ContentArea>
                  {!showResponse && (
                    <TimingContainer>
                      {entries.map((e, idx) => {
                        const pct =
                          maxDur > 0 ? (100 * (e.dur ?? 0)) / maxDur : 0;
                        const w = Math.max(6, Math.min(100, Math.round(pct)));
                        return (
                          <TimingEntry key={idx}>
                            <TimingName>
                              {e.name || text.na}
                              {e.isSlow && <SlowMarkerPill>!</SlowMarkerPill>}
                            </TimingName>
                            <TimingTrack>
                              <TimingBar $width={w} $isSlow={e.isSlow} />
                            </TimingTrack>
                            <TimingDuration>
                              {(e.dur ?? 0).toFixed(1)} ms
                            </TimingDuration>
                            {e.desc && (
                              <TimingDescription>{e.desc}</TimingDescription>
                            )}
                          </TimingEntry>
                        );
                      })}
                    </TimingContainer>
                  )}

                  {showResponse && hasJson && (
                    <JsonContainer>{jsonText}</JsonContainer>
                  )}
                </ContentArea>

                <Controls>
                  <Button
                    $size="small"
                    onClick={() =>
                      setIndex((i) => Math.min(groups.length - 1, i + 1))
                    }
                    disabled={safeIndex === groups.length - 1}
                  >
                    {text.buttons.prev}
                  </Button>

                  <Flex $gap={"1rem"}>
                    <Button
                      $size="small"
                      onClick={() => setShowResponse((v) => !v)}
                      disabled={!hasJson}
                    >
                      {showResponse
                        ? text.buttons.timings
                        : text.buttons.response}
                    </Button>
                    <Button
                      $size="small"
                      $variant="important"
                      onClick={() =>
                        setItems((prev) =>
                          prev.filter((i) => groupKey(i) !== g.key),
                        )
                      }
                    >
                      {text.buttons.dismiss}
                    </Button>
                  </Flex>

                  <Button
                    $size="small"
                    onClick={() => setIndex((i) => Math.max(0, i - 1))}
                    disabled={safeIndex === 0}
                  >
                    {text.buttons.next}
                  </Button>
                </Controls>
              </>
            );
          })()}
        </Panel>
      )}
    </>
  );
});
